import arrowIosBackFill from '@iconify/icons-eva/arrow-ios-back-fill'
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill'
import peopleFill from '@iconify/icons-eva/people-fill'
import { Icon } from '@iconify/react'
import { ChangeEvent, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
// material
import { Box, Drawer, IconButton, IconButtonProps, Stack, useMediaQuery } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'

import { IStaff, IStaff as Staff } from 'types/user'
//
import StaffAccount from './StaffAccount'
import StaffList from './StaffList'
import StaffSearch from './StaffSearch'

import { apiStaff } from 'apis/staff/staff'
import { useTimeSheetStore } from 'stores/timeSheet.store'
import { MHidden, MIconButton } from '../../../library/@material-extend'
import StaffSearchResults from './StaffSearchResults'

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 320
const COLLAPSE_WIDTH = 96

const ToggleButtonStyle = styled((props) => (
  <IconButton disableRipple {...props} />
))<IconButtonProps>(({ theme }) => ({
  left: 0,
  zIndex: 9,
  width: 32,
  height: 32,
  position: 'absolute',
  top: theme.spacing(13),
  borderRadius: `0 12px 12px 0`,
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  boxShadow: theme.customShadows.primary,
  '&:hover': {
    backgroundColor: theme.palette.primary.darker
  }
}))

// ----------------------------------------------------------------------

export default function StaffSidebar() {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [setStaffSelectedCode] = useTimeSheetStore((state) => [state.setStaffSelectedCode])
  const [openSidebar, setOpenSidebar] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearchFocused, setSearchFocused] = useState(false)
  const [staffList, setStaffList] = useState([])
  const displayResults = searchQuery && isSearchFocused
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isCollapse = !isMobile && !openSidebar
  useEffect(() => {
    doFetch()
  }, [])

  const doFetch = async () => {
    try {
      const res = await apiStaff.list()
      const data = res?.data?.data
      if (data.length > 0) {
        setStaffList(data)
      }
    } catch (error) {
      console.log(error)
    }
  }
  useEffect(() => {
    if (isMobile) {
      return handleCloseSidebar()
    }
    return handleOpenSidebar()
  }, [isMobile, pathname])

  useEffect(() => {
    if (!openSidebar) {
      return setSearchFocused(false)
    }
  }, [openSidebar])

  const handleOpenSidebar = () => {
    setOpenSidebar(true)
  }

  const handleCloseSidebar = () => {
    setOpenSidebar(false)
  }

  const handleToggleSidebar = () => {
    setOpenSidebar((prev) => !prev)
  }

  const handleClickAwaySearch = () => {
    setSearchFocused(false)
    setSearchQuery('')
  }

  const handleChangeSearch = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value.toLocaleLowerCase()
      setSearchQuery(value)
      if (value) {
        const normalizedValue = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        const response = staffList.filter((staff: IStaff) => {
          const normalizedName = staff.name
            .toLocaleLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
          return normalizedName.includes(normalizedValue)
        })
        setSearchResults(response)
      } else {
        setSearchResults([])
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleSearchFocus = () => {
    setSearchFocused(true)
  }

  const handleSearchSelect = (staffCode: string) => {
    setSearchFocused(false)
    setSearchQuery('')
    navigate('')
  }

  const handleSelectStaff = (result: Staff) => {
    setStaffSelectedCode(result.staffCode)
    // setSearchFocused(false)
    setSearchQuery('')
    navigate('')
  }

  const renderContent = (
    <div className="h-[calc(100vh-40px)] overflow-hidden">
      <Box sx={{ py: 2, px: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="center">
          {!isCollapse && (
            <>
              <StaffAccount />
              <Box sx={{ flexGrow: 1 }} />
            </>
          )}

          <MIconButton onClick={handleToggleSidebar}>
            <Icon
              width={20}
              height={20}
              icon={openSidebar ? arrowIosBackFill : arrowIosForwardFill}
            />
          </MIconButton>
        </Stack>

        {!isCollapse && (
          <StaffSearch
            query={searchQuery}
            onFocus={handleSearchFocus}
            onChange={handleChangeSearch}
            onClickAway={handleClickAwaySearch}
          />
        )}
      </Box>

      {!displayResults ? (
        <StaffList
          staffs={staffList}
          isOpenSidebar={openSidebar}
          // activeStaffCode={staffSelectedCode}
          sx={{ ...(isSearchFocused && { display: 'none' }) }}
        />
      ) : (
        <StaffSearchResults
          query={searchQuery}
          results={searchResults}
          onSelectStaff={handleSelectStaff}
        />
      )}
    </div>
  )
  if (staffList.length === 0) return <></>
  return (
    <div className=" mr-5">
      <MHidden width="mdUp">
        <ToggleButtonStyle onClick={handleToggleSidebar}>
          <Icon width={16} height={16} icon={peopleFill} />
        </ToggleButtonStyle>
      </MHidden>

      {/* Mobile */}
      <MHidden width="mdUp">
        <Drawer
          ModalProps={{ keepMounted: true }}
          open={openSidebar}
          onClose={handleCloseSidebar}
          sx={{
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      {/* Desktop */}
      <MHidden width="mdDown">
        <Drawer
          open={openSidebar}
          variant="persistent"
          sx={{
            width: DRAWER_WIDTH,
            transition: theme.transitions.create('width'),
            '& .MuiDrawer-paper': {
              position: 'static',
              width: DRAWER_WIDTH
            },
            ...(isCollapse && {
              width: COLLAPSE_WIDTH,
              '& .MuiDrawer-paper': {
                width: COLLAPSE_WIDTH,
                position: 'static',
                transform: 'none !important',
                visibility: 'visible !important'
              }
            })
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </div>
  )
}
